/**
 * THE TYPE SCALE — a brand kit's type system as five named roles, not two font names.
 *
 * WHAT WAS MISSING. A kit stored exactly two facts about type: a heading family and a body family. Every
 * other decision — how big a title is next to its supporting line, which face carries a label, whether
 * headings track tight or loose — was left to the generation prompt, which answers it the same way for
 * every brand. Five brands produced five RECOLOURINGS of one deck, the same failure `styleImport` already
 * documents for spacing. Type is the more visible half.
 *
 * WHY FIVE ROLES, AND THESE FIVE. They are the ones a slide actually uses, and they match the vocabulary
 * Guri pointed at (Chronicle's Display / Heading / Subheading / Paragraph / Small paragraph). Fewer and a
 * deck cannot separate a cover title from a section title; more and the extra rows are decisions nobody
 * makes.
 *
 * WHAT TRANSFERS FROM A WEBSITE, AND WHAT DOES NOT. A site's px sizes DO NOT transfer: granola.ai sets
 * body text at 14px, which on a 1920x1080 slide is unreadable. What transfers is everything else — the
 * RATIO between steps, the weights the brand actually ships, its tracking and leading, which face carries
 * which role. So `fromSiteCss` reads relationships and anchors them to slide-appropriate sizes, and never
 * copies a website's absolute px onto a slide. That is the same line `styleImport` draws when it refuses
 * to invent `type.scale` from two sizes.
 */

/** The five roles. Order is the render order and the size order, largest first. */
export const TYPE_ROLES = ['display', 'heading', 'subheading', 'body', 'caption'] as const

export type TypeRole = (typeof TYPE_ROLES)[number]

/** Which of the kit's two families a role is set in. */
export type TypeFace = 'display' | 'body'

export interface TypeStyle {
  face: TypeFace
  /** px, at 1920x1080 slide scale. */
  size: number
  weight: number
  /** em. Negative is tighter. Absent means the face's own default. */
  tracking?: number
  /** Unitless multiple of the size. */
  leading?: number
  case?: 'none' | 'upper'
  /** WHEN to reach for this role, in one line. Read by generation and editable by the user, because the
   *  rule is the half a model gets wrong on its own — it will happily set a caption in the display face. */
  usage?: string
}

export type TypeScale = Record<TypeRole, TypeStyle>

export const ROLE_LABELS: Record<TypeRole, string> = {
  display: 'Display',
  heading: 'Heading',
  subheading: 'Subheading',
  body: 'Paragraph',
  caption: 'Small paragraph',
}

/**
 * The default rules. Deliberately about PLACEMENT rather than appearance — a model can see the size and
 * the weight in the same block, so restating them here would be noise; what it cannot infer is that a
 * deck should hold one Display per deck and that a caption is never the display face.
 */
const DEFAULT_USAGE: Record<TypeRole, string> = {
  display: 'The cover title, and nothing else. One per deck.',
  heading: 'The claim at the top of a slide. Most slides have exactly one.',
  subheading: 'Section labels, eyebrows, and the label above a statistic.',
  body: 'Supporting sentences and descriptions. The default for prose.',
  caption: 'Footnotes, sources, axis labels, and slide numbers.',
}

/**
 * THE LEGAL RANGE FOR EACH ROLE, copied from ARTIFACT_CONTRACT's own TYPE SCALE section.
 *
 * These are not preferences, they are the sizes the contract already instructs every artifact to use:
 * "Hero title 140-200px. Section heading 80-120px. Page heading 56-80px. Body 32-44px. Caption or label
 * 22-28px." The contract additionally puts "body text never under 28px" on its NEVER-YIELD list.
 *
 * WHY THIS MATTERS MORE THAN IT LOOKS. The first version of this file anchored the scale at body 24 and
 * caption 16, which sits UNDER that floor. A kit therefore handed the model a type scale that contradicted
 * the contract it was also being handed, and the contract is the one that says it never yields. Two
 * instructions, one of them unfollowable. It also made every slide emptier: type a third smaller fills a
 * third less of a canvas whose size never changed, which is exactly the "why does this look so empty"
 * that prompted the check.
 *
 * So the BRAND decides where inside each range it sits, and the CONTRACT decides the range. A brand with a
 * dramatic ramp lands near the top of every band and a calm one near the bottom, and neither can produce a
 * slide the contract would reject.
 */
export const ROLE_BANDS: Record<TypeRole, { min: number; max: number }> = {
  display: { min: 140, max: 200 },
  heading: { min: 80, max: 120 },
  subheading: { min: 56, max: 80 },
  body: { min: 32, max: 44 },
  caption: { min: 22, max: 28 },
}

const clampToBand = (role: TypeRole, px: number) =>
  Math.round(Math.min(Math.max(px, ROLE_BANDS[role].min), ROLE_BANDS[role].max))

/**
 * Every role's size held inside its band, on a copy.
 *
 * The one clamp every path shares. `typeScaleFromCss` clamps as it derives, but a STORED scale arrives
 * from a JSON column that the Typography panel lets a person type into (8 to 400 px) and that an agent
 * op writes by arithmetic, so a 240px display can be sitting in a kit right now. The contract's bands
 * never yield, and the recipe now states the stored number as THE size (Guri, 2026-09-05: "the display
 * should also match the extraction of the PDF. Not hard-coded"), so a size outside the band would hand
 * the writer two instructions it cannot both follow. Weights, faces, tracking and usage pass through.
 */
export function clampScaleToBands(scale: TypeScale): TypeScale {
  const out: TypeScale = JSON.parse(JSON.stringify(scale))
  for (const role of TYPE_ROLES) {
    const size = out[role]?.size
    out[role].size = Number.isFinite(size) ? clampToBand(role, size) : BASE_SIZES[role]
  }
  return out
}

/** The slide-scale anchor: mid-band for every role, so a kit that knows nothing about its brand still
 *  produces a slide the contract is happy with. */
const BASE_SIZES: Record<TypeRole, number> = {
  display: 160,
  heading: 96,
  subheading: 64,
  body: 36,
  caption: 24,
}

const BASE_WEIGHTS: Record<TypeRole, number> = {
  display: 700,
  heading: 700,
  subheading: 500,
  body: 400,
  caption: 400,
}

const BASE_FACES: Record<TypeRole, TypeFace> = {
  display: 'display',
  heading: 'display',
  subheading: 'body',
  body: 'body',
  caption: 'body',
}

/** Headings lead tighter than prose; that relationship holds across every brand and is not worth a field
 *  until a brand contradicts it. */
const BASE_LEADING: Record<TypeRole, number> = {
  display: 1,
  heading: 1.1,
  subheading: 1.3,
  body: 1.5,
  caption: 1.4,
}

export function defaultTypeScale(): TypeScale {
  return TYPE_ROLES.reduce((out, role) => {
    out[role] = {
      face: BASE_FACES[role],
      size: BASE_SIZES[role],
      weight: BASE_WEIGHTS[role],
      leading: BASE_LEADING[role],
      usage: DEFAULT_USAGE[role],
    }
    return out
  }, {} as TypeScale)
}

// --- reading a brand's own CSS ----------------------------------------------------------------------

/** `--text-5xl:3rem` / `--text-base:14px` → px. rem is resolved at 16, the one assumption here and the
 *  same one `styleImport` makes. */
function lengthPx(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const s = raw.trim()
  const rem = /^(-?[\d.]+)rem$/i.exec(s)
  if (rem) return Number(rem[1]) * 16
  const px = /^(-?[\d.]+)px$/i.exec(s)
  if (px) return Number(px[1])
  return undefined
}

function emValue(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const m = /^(-?[\d.]+)em$/i.exec(raw.trim())
  return m ? Number(m[1]) : undefined
}

function unitless(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const n = Number(raw.trim())
  return Number.isFinite(n) && n > 0 && n < 4 ? n : undefined
}

/** Every `--name:value` in a stylesheet, last write winning, which is what the cascade would do for a
 *  single `:root` block and is close enough for a theme file. */
export function readCssVars(css: string): Record<string, string> {
  const out: Record<string, string> = {}
  for (const m of css.matchAll(/(--[a-z0-9_-]+)\s*:\s*([^;}]{1,120})/gi)) {
    out[m[1].toLowerCase()] = m[2].trim()
  }
  return out
}

/**
 * A brand's type scale, read off the design-system CSS its own site ships.
 *
 * Reads the RATIO and the details, never the absolute sizes — see the header. The largest declared step
 * over the body step gives the ramp; that ramp is then replayed from our own slide-scale body size, so a
 * brand with a dramatic 5x ramp gets a dramatic deck and a brand with a flat 2x ramp gets a calm one,
 * which is the difference that was being thrown away.
 *
 * Everything is optional and every miss falls back to {@link defaultTypeScale}. A site that ships no
 * theme variables costs us nothing.
 */
export function typeScaleFromCss(css: string, base: TypeScale = defaultTypeScale()): TypeScale {
  const v = readCssVars(css)
  const scale: TypeScale = JSON.parse(JSON.stringify(base))

  // The declared size ramp, smallest to largest. Tailwind's `--text-*` is the common shape; a hand-rolled
  // system usually names them the same way.
  const steps = Object.entries(v)
    .filter(([k]) => /^--text-[a-z0-9]+$/.test(k) && !k.includes('--line-height') && !k.includes('--letter'))
    .map(([k, raw]) => ({ key: k, px: lengthPx(raw) }))
    .filter((s): s is { key: string; px: number } => typeof s.px === 'number' && s.px > 0)
    .sort((a, b) => a.px - b.px)

  if (steps.length >= 3) {
    const smallest = steps[0].px
    const largest = steps[steps.length - 1].px
    const ramp = largest / smallest
    // Replay the brand's ramp from OUR body size, then hold every role inside the contract's band. The
    // clamp on `spread` still earns its place: it keeps a site with a 20x ramp between a legal line and a
    // hero from pinning every role to its ceiling and flattening the very hierarchy we came for.
    const spread = Math.min(Math.max(ramp, 1.6), 5)
    const body = scale.body.size
    scale.display.size = clampToBand('display', body * spread)
    scale.heading.size = clampToBand('heading', body * (1 + (spread - 1) * 0.62))
    scale.subheading.size = clampToBand('subheading', body * (1 + (spread - 1) * 0.16))
    scale.caption.size = clampToBand('caption', body * 0.68)
  }

  // Weights the brand actually declares. A system that tops out at 500 must not get a 700 heading — that
  // is faux-bold, and it is the single most visible way a deck stops looking like the brand.
  const weights = Object.entries(v)
    .filter(([k]) => /^--font-weight-/.test(k))
    .map(([, raw]) => Number(raw.trim()))
    .filter((n) => Number.isFinite(n) && n >= 100 && n <= 900)
    .sort((a, b) => a - b)
  if (weights.length) {
    const heaviest = weights[weights.length - 1]
    const regular = weights.find((w) => w >= 400) ?? weights[0]
    const mid = weights.find((w) => w >= 500) ?? heaviest
    scale.display.weight = heaviest
    scale.heading.weight = heaviest
    scale.subheading.weight = mid
    scale.body.weight = regular
    scale.caption.weight = regular
  }

  // Display tracking. A brand that sets `--tracking-tight` means it for its big type, and this is exactly
  // what the backend's single `headingLetterSpacing` gets wrong: granola.ai stores `.02em` (read off a
  // 12px caption rule) while its display type is set at `-.025em`. Positive tracking on a large serif is
  // the "looks weird" everyone notices and nobody can name.
  const tight = emValue(v['--tracking-tight'])
  if (typeof tight === 'number') {
    scale.display.tracking = tight
    scale.heading.tracking = tight
  }
  const wide = emValue(v['--tracking-wide'] ?? v['--tracking-wider'])
  if (typeof wide === 'number') scale.caption.tracking = wide

  const leadTight = unitless(v['--leading-tight'])
  if (leadTight) {
    scale.display.leading = Math.min(leadTight, 1.15)
    scale.heading.leading = leadTight
  }
  const leadNormal = unitless(v['--leading-normal'])
  if (leadNormal) scale.body.leading = leadNormal

  return scale
}

/**
 * The scale a kit should show, given what it already knows.
 *
 * Layers, weakest first: the slide-scale defaults, then the brand's own CSS variables when we have them,
 * then the handful of exact facts the backend extractor already stored. The extractor's facts win because
 * they were read off the rendered page rather than off a theme file — with ONE exception, tracking, where
 * the theme variable is the more trustworthy of the two (see `typeScaleFromCss`).
 */
export function deriveTypeScale(input: {
  css?: string | null
  headingFacts?: Record<string, string> | null
  /** Weights the DISPLAY family ships, and the BODY family's, separately. They are different files and
   *  routinely different lists — granola.ai ships one Quadrant (400) against five Melange (300-600), so a
   *  single merged list would either faux-bold the display face or cap the body face at 400. */
  displayWeights?: readonly string[] | null
  bodyWeights?: readonly string[] | null
  stored?: TypeScale | null
}): TypeScale {
  if (input.stored) return input.stored
  const scale = input.css ? typeScaleFromCss(input.css) : defaultTypeScale()
  const facts = input.headingFacts ?? {}

  // A heading size read off the page is a real measurement, but it is a WEBSITE size. Use it only for the
  // ratio it implies against the site's own body text, never as a slide size.
  const lead = unitless(facts.headingLineHeight)
  if (lead) {
    scale.heading.leading = lead
    scale.display.leading = Math.min(lead, 1.15)
  }
  if (facts.headingTransform && /upper/i.test(facts.headingTransform)) {
    scale.display.case = 'upper'
    scale.heading.case = 'upper'
  }

  return snapScaleWeights(scale, input)
}

/**
 * THE ONE DERIVATION of a kit's type scale, used by the Typography panel, the kit agent's `scaleOf` and
 * `styleFromBrandKit` alike.
 *
 * Before this there were three: the panel derived a scale for DISPLAY and never wrote it, the host derived
 * one for an edit to land on, and the recipe only ever read a STORED one. So a kit nobody had opened the
 * Typography page of handed the writer families and weights and no size at all, while the page showed
 * five sizes that generation never saw (Guri, 2026-09-05: "the font size that is there as a title should
 * match the display"). Three derivations agreeing by convention is the shape that drifts; one function is
 * what makes the number on the panel the number in the document.
 *
 * Stored wins whole, else the extractor's facts snapped to the faces the kit ships, else the defaults —
 * exactly `deriveTypeScale`'s ladder — and the result is ALWAYS held inside `ROLE_BANDS`, which is what
 * lets the recipe state it as law rather than as a suggestion the contract then overrules.
 */
export function typeScaleForKit(input: {
  typeScale?: TypeScale | null
  /** `BrandStyle.typography`: the backend's measured heading facts. */
  typography?: Record<string, string> | null
  /** `BrandStyle.fontFaces`: the files the kit ships, for the weight snap. */
  fontFaces?: readonly { family: string; weight?: string }[] | null
  headingFont?: string | null
  bodyFont?: string | null
}): TypeScale {
  return clampScaleToBands(
    deriveTypeScale({
      stored: input.typeScale ?? null,
      headingFacts: input.typography ?? null,
      displayWeights: weightsForFamily(input.fontFaces, input.headingFont),
      bodyWeights: weightsForFamily(input.fontFaces, input.bodyFont),
    }),
  )
}

/**
 * Move every role's weight to the nearest one its own FACE actually ships.
 *
 * The difference between a real 700 and the browser smearing a 400 into a fake one — which is what a deck
 * set in granola's Quadrant does on every heading, because that family ships exactly one file. Separate
 * from `deriveTypeScale` because it has to run on a scale that came from anywhere: a fresh derivation, a
 * scale read off the brand's website, or a stored one whose fonts have since been swapped.
 *
 * A face with no known weights is left alone. Guessing there would be worse than the default.
 */
export function snapScaleWeights(
  scale: TypeScale,
  weights: { displayWeights?: readonly string[] | null; bodyWeights?: readonly string[] | null },
): TypeScale {
  const fitter = (list: readonly string[] | null | undefined) => {
    const shipped = (list ?? []).map(Number).filter((n) => Number.isFinite(n))
    if (!shipped.length) return undefined
    return (want: number) =>
      shipped.reduce((best, w) => (Math.abs(w - want) < Math.abs(best - want) ? w : best), shipped[0])
  }
  const fitDisplay = fitter(weights.displayWeights)
  const fitBody = fitter(weights.bodyWeights)
  const out: TypeScale = JSON.parse(JSON.stringify(scale))
  for (const role of TYPE_ROLES) {
    const fit = out[role].face === 'display' ? fitDisplay : fitBody
    if (fit) out[role].weight = fit(out[role].weight)
  }
  return out
}

/** The weights one family's `@font-face` files ship, ready for {@link snapScaleWeights}. Matches on the
 *  LEAD family of a CSS stack, because a kit stores `'Melange', sans-serif` as readily as `Melange`. */
export function weightsForFamily(
  faces: readonly { family: string; weight?: string }[] | null | undefined,
  family: string | null | undefined,
): string[] | undefined {
  const lead = family?.split(',')[0]?.replace(/['"]/g, '').trim().toLowerCase()
  if (!lead || !faces?.length) return undefined
  const out = [
    ...new Set(
      faces
        .filter((f) => {
          const name = f.family?.trim().toLowerCase()
          // Prefix either way, the same relationship `brandFonts.aliasTarget` uses: a kit declaring
          // "Quadrant Notepad" must find the weights of the file called "Quadrant".
          return !!name && (name === lead || name.startsWith(lead) || lead.startsWith(name))
        })
        .map((f) => (f.weight === 'bold' ? '700' : !f.weight || f.weight === 'normal' ? '400' : f.weight)),
    ),
  ]
  return out.length ? out : undefined
}

// --- font families off a brand's own CSS ------------------------------------------------------------

export interface SiteFonts {
  /** The family headings are set in, first-in-stack and unquoted. */
  display?: string
  /** The family body copy is set in. */
  body?: string
  /** Every family the site ships a real file for, icon fonts excluded. */
  shipped: string[]
  /** Weights the site declares, as strings, ascending. */
  weights: string[]
  /**
   * The actual FONT FILES, ready for `brandStyle.fontFaces`.
   *
   * Without these a site read gives a NAME with nothing behind it, and the kit renders "Anthropic Sans"
   * in a system serif — the exact failure the granola work started from, arriving through the new door.
   * A family only exists once a file answers to it.
   */
  faces: { family: string; url: string; weight?: string; style?: string }[]
}

/**
 * Faces that are never a brand's typeface, however prominently a page ships them.
 *
 * Two kinds, both measured. ICON SETS: `webflow-icons` arrived first in anthropic.com's stylesheet and
 * would have been handed back as the brand face. VENDOR LIBRARIES: duolingo.com bundles KaTeX, so once
 * relative urls started resolving, the reader confidently answered `KaTeX_AMS` for display and
 * `KaTeX_Caligraphic` for body — a regression from finding nothing, because a wrong answer is acted on
 * and an empty one is not.
 *
 * The list is deliberately of KNOWN libraries rather than a clever heuristic. A brand really can call its
 * face anything, and guessing by shape would eventually throw away a real one.
 */
const ICON_FAMILY =
  /(icon|glyph|symbol|fontawesome|font-awesome|material|katex|mathjax|remixicon|feathericon|lucide|bootstrap|ionicons|typicons)/i

/** The first family in a stack, unquoted: `"Anthropic Sans",Arial,sans-serif` -> `Anthropic Sans`. */
function leadOf(stack: string | undefined): string | undefined {
  const lead = stack?.split(',')[0]?.replace(/['"]/g, '').trim()
  if (!lead || /^(inherit|initial|unset|revert|none)$/i.test(lead)) return undefined
  // A bare generic is the fallback, never the brand.
  if (/^(sans-serif|serif|monospace|cursive|fantasy|system-ui|ui-[a-z-]+)$/i.test(lead)) return undefined
  // NOT EVERY VARIABLE WHOSE NAME SAYS "text" HOLDS A FAMILY. anthropic.com's body font came back as the
  // string "0px", off a var matching the body word list that holds a length. Same class of failure
  // `brandKitWire.sanitizeBrandFont` guards on the other side of the wire: a length, a number, a colour
  // or a CSS function is never a font family, and passing one on writes `font-family:'0px'` into the
  // generation prompt as an instruction the model tries to follow.
  if (/^[-+.]?\d/.test(lead)) return undefined
  if (/^#|[(){};:]/.test(lead)) return undefined
  if (!/[a-z]/i.test(lead)) return undefined
  if (lead.length > 64) return undefined
  return lead
}

/**
 * The families a brand sets its type in, read off its own CSS.
 *
 * THE GAP THIS CLOSES (anthropic.com, measured 2026-08-18). A kit built from a site with no backend
 * extraction showed "Not set" for both Heading and Body, while the site declares
 * `--_typography---font--display-sans: "Anthropic Sans", Arial, sans-serif` and ships a real
 * `@font-face` for it. Everything needed was in the stylesheet; nothing was looking for it.
 *
 * DELIBERATELY NAMING-AGNOSTIC. The type SCALE reader keys on Tailwind's `--text-*` convention, which is
 * why anthropic.com answered "no type system found": it is a Webflow site and its variables are called
 * `--_typography---font--display-sans`. So this matches on the WORDS inside a name, wherever they sit,
 * and falls back to the `@font-face` list when no variable is recognisable at all.
 *
 * A declared family is cross-checked against the shipped files the same way `styleImport.resolveFamily`
 * does, because a name no file answers to renders as a fallback however confidently it was declared.
 */
export function fontFamiliesFromCss(css: string): SiteFonts {
  const vars = readCssVars(css)

  // Every @font-face block, parsed whole: family, the first real file url, weight and style. Metric
  // adjustment faces (`melange Fallback` = `local("Arial")` with overrides) ship no glyphs, and a
  // data: url is an icon set inlined by a page builder, never a brand text face.
  const faces: SiteFonts['faces'] = []
  for (const block of css.matchAll(/@font-face\s*\{([^}]*)\}/gi)) {
    const body = block[1]
    const family = /font-family\s*:\s*([^;}]+)/i.exec(body)?.[1]?.replace(/['"]/g, '').trim()
    if (!family || ICON_FAMILY.test(family)) continue
    if (/(^|[\s\-_])fallback([\s\-_]|$)/i.test(family)) continue
    const url = /url\(\s*['"]?(https?:\/\/[^'")]+)['"]?\s*\)/i.exec(body)?.[1]
    if (!url) continue
    const weight = /font-weight\s*:\s*([^;}]+)/i.exec(body)?.[1]?.trim()
    const style = /font-style\s*:\s*([^;}]+)/i.exec(body)?.[1]?.trim()
    faces.push({ family, url, ...(weight ? { weight } : {}), ...(style ? { style } : {}) })
  }
  const shipped = [...new Set(faces.map((f) => f.family))]

  /** Follow `var(--a)` to a literal. Without this the picker hands back the indirection itself:
   *  anthropic.com's body font came out as the string "var(--_typography---font--display-sans)". */
  const deref = (value: string | undefined, hops = 6): string | undefined => {
    let current = value
    for (let i = 0; i < hops; i += 1) {
      const ref = /^var\(\s*(--[a-z0-9_-]+)\s*(?:,\s*([^)]+))?\)$/i.exec((current ?? '').trim())
      if (!ref) return current
      current = vars[ref[1].toLowerCase()] ?? ref[2]?.trim()
    }
    return current
  }

  /**
   * The family a set of role words points at.
   *
   * SHORTEST NAME WINS among the matches, because a design system qualifies its secondary faces and
   * leaves its primary one plain. anthropic.com declares both `--_typography---font--display-sans` and
   * `--_typography---font--display-serif-family`; taking the first match in declaration order picked the
   * SERIF, and the site's headings are set in the sans.
   */
  const pick = (words: RegExp): string | undefined => {
    const hits = Object.entries(vars)
      .filter(([name]) => words.test(name))
      .map(([name, value]) => ({ name, raw: deref(value) ?? '', lead: leadOf(deref(value)) }))
      .filter((h): h is { name: string; raw: string; lead: string } => !!h.lead)
      // PLAUSIBILITY, because a role word in a variable NAME proves nothing about its value. granola's
      // body font came back as "white": a var matching the body word list holds a colour keyword, and a
      // bare word passes every syntactic check a family name has. So a pick has to be either a face the
      // site actually ships, or written the way a font stack is written — quoted, or a comma list.
      .filter((h) => isShipped(h.lead) || /[",]/.test(h.raw))
      // A SHIPPED family outranks a shorter name. granola declares `--font-sans: "KMR Melange Grotesk"`
      // and `--font-melange: "melange"`; the first has the shorter variable name and the second is the
      // one with a file behind it. A name no file answers to renders as a fallback however plainly the
      // brand declared it, which is the whole lesson of the Quadrant Notepad bug.
      .sort((a, b) => Number(shippedMatch(b.lead) !== undefined) - Number(shippedMatch(a.lead) !== undefined) || a.name.length - b.name.length)
    return hits[0]?.lead
  }

  /** A family the site ships a real file for — exact, or one name a prefix of the other, which is what a
   *  marketing name over a file name looks like (`Quadrant Notepad` over `quadrant`). */
  const shippedMatch = (declared: string): string | undefined => {
    const d = declared.toLowerCase()
    return shipped.find((f) => {
      const s = f.toLowerCase()
      return s === d || s.startsWith(d) || d.startsWith(s)
    })
  }
  // NO ESCAPE HATCH FOR A SITE THAT SHIPS NOTHING. This used to answer true when `shipped` was empty,
  // which switched the plausibility filter off entirely for exactly the sites that need it most: with no
  // faces to check against, `--body-default: white` sailed through and became the brand's body font.
  // A site with no @font-face has to earn the pick by writing a real stack instead.
  const isShipped = (declared: string) => shipped.length > 0 && !!shippedMatch(declared)

  const resolveShipped = (declared: string | undefined): string | undefined =>
    declared ? (shippedMatch(declared) ?? declared) : undefined

  // Display first: a name saying "display", "heading" or "title" is the strongest statement a system
  // makes about which face carries its largest type.
  const declaredDisplay = pick(/(display|heading|headline|title|hero)/i)
  const declaredBody = pick(/(body|paragraph|detail|copy|prose|text|base|default)/i)

  const display = resolveShipped(declaredDisplay) ?? shipped[0]

  /**
   * The body face, with a PAIRING rule when the site ships exactly two.
   *
   * A brand shipping two text faces is pairing them, and the one that is not the display face is the one
   * prose is set in. anthropic.com declares `--_typography---font--detail: "Anthropic Sans"`, so the
   * name-based pick answered Sans for BOTH roles while the site's own paragraphs are set in Anthropic
   * Serif. Variable names describe a system's slots; what the pair IS can only be read off the files.
   *
   * Only with exactly two, deliberately. Three or more and which is the prose face is a real question
   * the file list cannot answer, so the declared name stands.
   */
  const declared = resolveShipped(declaredBody)
  const textFaces = shipped.filter((f) => !/mono|code/i.test(f))
  const paired = textFaces.length === 2 ? textFaces.find((f) => f !== display) : undefined
  const body = (declared && declared !== display ? declared : undefined) ?? paired ?? declared ?? shipped[1] ?? display

  const weights = [
    ...new Set(
      Object.entries(vars)
        .filter(([name]) => /(weight|bold|semibold|medium|regular|light|black)/i.test(name))
        .map(([, v]) => v.trim())
        .filter((v) => /^[1-9]00$/.test(v)),
    ),
  ].sort((a, b) => Number(a) - Number(b))

  return {
    ...(display ? { display } : {}),
    ...(body ? { body } : {}),
    shipped,
    weights,
    faces,
  }
}

/**
 * Does this stylesheet say anything about type at all?
 *
 * Broader than the `--text-*` check it replaces, which was a Tailwind-shaped assumption wearing a general
 * name: anthropic.com declares a full type system and answered "no type system found" purely because its
 * variables are Webflow-named. A shipped `@font-face` is on its own enough to say yes — a site that ships
 * its own typeface has told us the most important thing about its type.
 */
export function hasTypeSignal(css: string): boolean {
  if (/@font-face/i.test(css)) return true
  const vars = readCssVars(css)
  return Object.keys(vars).some((k) => /(font|text|type|leading|tracking|line-height|size)/i.test(k))
}
